import { HastNode, isAllowedAnswerUrl } from './answerUrl';

// A link in a bot answer sits in a trusted-looking bubble, so one that a prompt injection in indexed
// help content or tool data put there is one-click phishing, with whatever the model wrote into the
// URL as the payload (#2856). The server neutralises such links before an answer is stored; this is
// the same rule on the renderer, for answers stored before that and as a second line should a path
// miss it. Allowed are the page's own origin (an anchor, a relative path, the [attachment:n](table)
// marker), the hosts restyle links may point at, and the hosts of the answer's own cited sources,
// which the sources list under the answer links to anyway.

/**
 * The hosts of an answer's cited sources. Only an http(s) URL contributes: a mailto: or urn: source
 * has an empty hostname, which as a domain would allow every host ending in a dot.
 */
export function sourceHostsOf(sourceUrls: ReadonlyArray<string | undefined>): string[] {
    const hosts: string[] = [];
    for (const sourceUrl of sourceUrls) {
        if (!sourceUrl) {
            continue;
        }
        try {
            const url = new URL(sourceUrl);
            if ((url.protocol === 'https:' || url.protocol === 'http:') && url.hostname) {
                hosts.push(url.hostname);
            }
        } catch {
            // Not a URL, so nothing to allow.
        }
    }
    return hosts;
}

const textOf = (node: HastNode): string =>
    node.type === 'text' ? (node.value ?? '') : (node.children ?? []).map(textOf).join('');

// A mailto: link stays only when its text shows the whole target, which is what remark-gfm makes of a
// bare address; one whose text hides where it goes is unwrapped.
const isWrittenOutMailto = (href: string, text: string): boolean => {
    const target = href.toLowerCase();
    const shown = text.trim().toLowerCase();
    return target.startsWith('mailto:') && (shown === target || `mailto:${shown}` === target);
};

// Markdown and raw-HTML links alike; a link to another host is replaced by its text.
const restrictLinkHosts = (sourceHosts: readonly string[]) => {
    const restrict = (node: HastNode): void => {
        if (!node.children) {
            return;
        }
        node.children = node.children.flatMap((child): HastNode[] => {
            restrict(child);
            const href = child.properties?.href;
            if (
                child.tagName !== 'a' ||
                typeof href !== 'string' ||
                isAllowedAnswerUrl(href, window.location.href, sourceHosts) ||
                isWrittenOutMailto(href, textOf(child))
            ) {
                return [child];
            }
            return child.children ?? [];
        });
    };
    return restrict;
};

/** Rehype plugin for every view that renders bot answers, since each one makes their links clickable. */
export const rehypeRestrictLinkHosts = (options?: { sourceHosts?: readonly string[] }) =>
    restrictLinkHosts(options?.sourceHosts ?? []);
